import {
  Field,
  Text,
  useSitecoreContext,
} from "@sitecore-jss/sitecore-jss-nextjs";
import clsx from "clsx";
import { useSearchParams } from "next/navigation";
import { useMemo } from "react";
import { Link } from "@repo/next-utils/router";
import { ComponentProps } from "@repo/sitecore-lib/component-props";
import * as Flex from "@repo/ui/flex";
import { Pagination } from "@/components/pagination";
import {
  TYPOGRAPHY_BODY2,
  TYPOGRAPHY_BODY2_BOLD,
  TYPOGRAPHY_BODY3_SENTENCE,
  TYPOGRAPHY_LABEL3,
} from "@/theme/topshop/typography/typography-constants";
import { ArrowR } from "../../../icons";
import { SEARCH_RESULTS_LIMIT } from "./constants";
import { useSearch } from "./search-provider";
import styles from "./search.module.css";

type SearchResultProps = ComponentProps & {
  fields: {
    data: { contextItem: { searchResultsHeading: Field<string> } };
  };
  searchResultsLength: number;
};

const SearchResult = ({
  fields: {
    data: {
      contextItem: { searchResultsHeading },
    },
  },
}: SearchResultProps): JSX.Element => {
  const searchParams = useSearchParams();
  const { searchResults, query, totalSearchItems, currentPage } = useSearch();
  const {
    sitecoreContext: { route },
  } = useSitecoreContext();

  const searchResultsLength = searchResults?.length;

  const params = useMemo(
    () => new URLSearchParams(searchParams?.toString() || ""),
    [searchParams],
  );

  const totalPages = Math.ceil(totalSearchItems! / SEARCH_RESULTS_LIMIT);

  const pages = useMemo(
    () =>
      Array.from({ length: totalPages }, (_, i) => {
        const pageNumber = i + 1;
        const pageParams = new URLSearchParams(searchParams?.toString() || "");

        if (pageNumber === 1) {
          pageParams.delete("page");
        } else {
          pageParams.set("page", pageNumber.toString());
        }

        return {
          number: pageNumber,
          key: `page-${pageNumber}-${pageParams.get("cid") || ""}`,
          query: Object.fromEntries(pageParams.entries()),
        };
      }),
    [totalPages, searchParams],
  );

  const { noResultsHeading, noResultsText } =
    (route?.fields as {
      noResultsHeading: Field<string>;
      noResultsText: Field<string>;
    }) || {};

  const NotFound = (
    <div className={styles.resultContainer}>
      <Flex.Container
        className={clsx(styles.nothingMatchesLabel, TYPOGRAPHY_BODY2)}
        gap="2xs"
      >
        <Text field={noResultsHeading} tag="p" />
        <span className={clsx(TYPOGRAPHY_BODY2_BOLD)}>"{query}"</span>
      </Flex.Container>
      <Text
        field={noResultsText}
        tag="p"
        className={clsx(styles.notFoundlabel, TYPOGRAPHY_BODY3_SENTENCE)}
      />
    </div>
  );

  return (
    <Flex.Container style={{ width: "100%" }} column gap="xl">
      <Flex.Container justify="space-between">
        <p className={clsx(TYPOGRAPHY_BODY2, styles.resultContainer)}>
          <Text tag="span" field={searchResultsHeading} />
          <span className={clsx(TYPOGRAPHY_BODY2_BOLD)}>"{query}"</span>
        </p>
        <Flex.Container justify="start">
          {!!searchResultsLength && (
            <p className={clsx(TYPOGRAPHY_BODY2, styles.foundLabel)}>
              {totalSearchItems} FAQs Found
            </p>
          )}
        </Flex.Container>
      </Flex.Container>
      {!!searchResultsLength && (
        <Flex.Container column justify="start" gap="sm">
          {searchResults?.map(({ title, href }) => (
            <Link
              key={title}
              className={clsx(TYPOGRAPHY_LABEL3, styles.foundTopic)}
              href={href}
            >
              <Flex.Container justify="space-between">
                <p>{title}</p>
                <ArrowR fontSize={20} />
              </Flex.Container>
            </Link>
          ))}
        </Flex.Container>
      )}
      {searchResults !== undefined && !searchResultsLength && NotFound}
      {searchResults !== undefined &&
        !!searchResultsLength &&
        totalPages > 1 && (
          <Pagination
            pages={pages}
            searchParams={params}
            currentPage={currentPage}
          />
        )}
    </Flex.Container>
  );
};

export default SearchResult;
